import React, { useRef, useEffect } from 'react';
import {
  Drawer,
  IconButton,
  Typography,
  Button,
  Box,
  makeStyles,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import ShareIcon from '@mui/icons-material/IosShare';
import AddBoxIcon from '@mui/icons-material/AddBox';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import usePWAInstall from '../../hooks/usePWAInstall';
import usePWABranding from '../../hooks/usePWABranding';

const useStyles = makeStyles((theme) => ({
  drawer: {
    '& .MuiDrawer-paper': {
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
      maxHeight: '85vh',
      paddingBottom: theme.spacing(2),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
    minWidth: 0,
  },
  appIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    flexShrink: 0,
    objectFit: 'cover',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.125rem',
  },
  content: {
    padding: theme.spacing(0, 2, 2),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.9375rem',
    marginBottom: theme.spacing(2),
  },
  stepsList: {
    paddingTop: 0,
  },
  stepIcon: {
    minWidth: 40,
    color: theme.palette.primary.main,
  },
  installButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    textTransform: 'none',
    fontWeight: 600,
  },
}));

/** Final fallback icon if configured icon fails to load (matches index.html apple-touch-icon). */
const FALLBACK_ICON_SRC = '/icons/icon-192.png';

/**
 * PWA install prompt: mobile-only bottom sheet.
 * Uses only configured system branding (public settings + company PWA API), never document.title
 * or hardcoded/old names.
 *
 * Fallback order: (1) configured PWA name/icon, (2) manifest default, (3) static fallback.
 * Validation: modal title = installPromptDisplayName; icon = installPromptIconUrl; no tab/cookie name.
 */
const PWAInstallPrompt = () => {
  const classes = useStyles();
  const iconFallbackTried = useRef(false);
  const {
    installPromptDisplayName,
    installPromptIconUrl,
    themeColor: pwaThemeColor,
  } = usePWABranding();
  const { shouldShow, isIOS, isAndroid, install, dismiss, deferredPrompt } = usePWAInstall();
  const appName =
    installPromptDisplayName && String(installPromptDisplayName).trim()
      ? installPromptDisplayName.trim()
      : 'PingoChat';
  const iconSrc =
    installPromptIconUrl && String(installPromptIconUrl).trim()
      ? installPromptIconUrl.trim()
      : FALLBACK_ICON_SRC;

  useEffect(() => {
    iconFallbackTried.current = false;
  }, [iconSrc]);

  const handleIconError = (e) => {
    const img = e.target;
    if (iconFallbackTried.current) return;
    iconFallbackTried.current = true;
    img.src = img.src === FALLBACK_ICON_SRC ? '/favicon.ico' : FALLBACK_ICON_SRC;
    img.style.borderRadius = '12px';
  };

  const handleInstall = async () => {
    await install();
    dismiss();
  };

  const handleClose = () => {
    dismiss();
  };

  if (!shouldShow) return null;

  return (
    <Drawer
      className={classes.drawer}
      anchor="bottom"
      open={shouldShow}
      onClose={handleClose}
      disableSwipeToOpen
    >
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <img src={iconSrc} alt="" className={classes.appIcon} onError={handleIconError} />
          <Box minWidth={0}>
            <Typography className={classes.title}>Instalar {appName}</Typography>
            <Typography className={classes.subtitle} style={{ marginBottom: 0 }}>
              Use {appName} like a native app on your phone.
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={handleClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={classes.content}>
        {isAndroid && (
          <>
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<GetAppIcon />}
              className={classes.installButton}
              onClick={handleInstall}
              disabled={!deferredPrompt}
              style={pwaThemeColor ? { backgroundColor: pwaThemeColor, color: '#fff' } : undefined}
            >
              Install App
            </Button>
          </>
        )}

        {isIOS && (
          <>
            <Typography className={classes.subtitle}>
              Follow instructions to add to your home screen.
            </Typography>
            <List className={classes.stepsList} dense>
              <ListItem alignItems="flex-start">
                <ListItemIcon className={classes.stepIcon}>
                  <ShareIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Step 1: Tap Share icon"
                  secondary="In the Safari toolbar (bottom or top)"
                />
              </ListItem>
              <ListItem alignItems="flex-start">
                <ListItemIcon className={classes.stepIcon}>
                  <AddBoxIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary='Step 2: Tap "Add to Home Screen"'
                  secondary="Scroll the menu if needed"
                />
              </ListItem>
              <ListItem alignItems="flex-start">
                <ListItemIcon className={classes.stepIcon}>
                  <TouchAppIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary='Step 3: Tap "Add"' secondary="In the top right corner" />
              </ListItem>
            </List>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default PWAInstallPrompt;
